/** Token namespaces for bound text templates in heading/paragraph labels. */
export type BoundTextNamespace = 'v' | 's' | 'f';

export interface BoundTextToken {
  raw: string;
  namespace: BoundTextNamespace;
  key: string;
  /** Optional display label from {{ns.key[Label]}}. */
  label?: string;
  index: number;
  length: number;
}

/**
 * Matches {{v.key}}, {{s.key}}, {{f.key}} with optional [Label].
 * Key: letters, digits, underscore.
 */
const TOKEN_RE = /\{\{(v|s|f)\.([A-Za-z0-9_]+)(?:\[([^\]]*)\])?\}\}/g;

export function hasBoundTextTokens(text: string | null | undefined): boolean {
  if (!text) {
    return false;
  }
  TOKEN_RE.lastIndex = 0;
  return TOKEN_RE.test(text);
}

export function parseBoundTextTokens(text: string): BoundTextToken[] {
  const tokens: BoundTextToken[] = [];
  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(text)) !== null) {
    const label = match[3];
    tokens.push({
      raw: match[0],
      namespace: match[1] as BoundTextNamespace,
      key: match[2],
      ...(label !== undefined ? { label } : {}),
      index: match.index,
      length: match[0].length,
    });
  }
  return tokens;
}

export function extractBoundTextKeys(
  text: string,
  namespace: BoundTextNamespace,
): string[] {
  const keys = new Set<string>();
  for (const token of parseBoundTextTokens(text)) {
    if (token.namespace === namespace) {
      keys.add(token.key);
    }
  }
  return [...keys];
}

export function formatBoundTextToken(
  namespace: BoundTextNamespace,
  key: string,
  label?: string,
): string {
  if (label !== undefined && label !== '') {
    return `{{${namespace}.${key}[${label}]}}`;
  }
  return `{{${namespace}.${key}}}`;
}

/**
 * Replace tokens using a resolver. Unresolved / null / undefined → empty string.
 * Display labels are ignored; resolution uses namespace + key only.
 */
export function resolveBoundTextTemplate(
  text: string,
  resolve: (
    namespace: BoundTextNamespace,
    key: string,
  ) => string | number | boolean | null | undefined,
): string {
  TOKEN_RE.lastIndex = 0;
  return text.replace(
    TOKEN_RE,
    (_raw, namespace: string, key: string, _label?: string) => {
      const value = resolve(namespace as BoundTextNamespace, key);
      if (value === undefined || value === null) {
        return '';
      }
      return String(value);
    },
  );
}

/** Slash trigger: / at start or after whitespace; not mid-token like http:// */
export function findSlashTrigger(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  if (caret < 1) {
    return null;
  }
  const before = text.slice(0, caret);
  const slashIndex = before.lastIndexOf('/');
  if (slashIndex < 0) {
    return null;
  }
  if (slashIndex > 0) {
    const prev = before[slashIndex - 1];
    if (prev && !/\s/.test(prev)) {
      return null;
    }
  }
  const query = before.slice(slashIndex + 1);
  if (/[\n\r]/.test(query)) {
    return null;
  }
  return { start: slashIndex, query };
}
